/* =====================================================================
 * createAuthProvider — single-entry factory that dispatches on
 * `config.kind` and delegates to the concrete factory.
 *
 * This is the canonical way for the app to turn a tenant-manifest
 * entry into a runtime `AuthProvider`. Keeping the dispatch here
 * (rather than in every consumer) means new kinds only need to touch
 * one switch branch in this package; callers never see
 * `config.kind === 'google_oauth'` string checks.
 *
 * The factory returns `AuthProvider`, not `AuthProvider | undefined`;
 * an unknown `kind` falls through to `assertNever` so TypeScript
 * guarantees exhaustiveness at compile time.
 * ===================================================================== */

import { googleOAuthProvider } from './providers/google';
import {
  emailPasswordProvider,
  knoxProvider,
  oidcProvider,
  samlProvider,
} from './providers/stubs';
import type { AuthProvider, AuthProviderConfig } from './types';

function assertNever(value: never): never {
  throw new Error(
    `[auth] Unhandled provider kind: ${JSON.stringify(value)}. ` +
      'Every AuthProviderConfig variant must have a factory.',
  );
}

/** Turn a typed config into a runtime provider.  */
export function createAuthProvider(config: AuthProviderConfig): AuthProvider {
  switch (config.kind) {
    case 'google_oauth':
      return googleOAuthProvider(config);
    case 'saml':
      return samlProvider(config);
    case 'oidc':
      return oidcProvider(config);
    case 'email_password':
      return emailPasswordProvider(config);
    case 'knox':
      return knoxProvider(config);
    default:
      return assertNever(config);
  }
}

/** Build a provider list from a list of configs. Preserves input
 *  order, which is also the display order in the UI. */
export function createAuthProviders(
  configs: readonly AuthProviderConfig[],
): AuthProvider[] {
  return configs.map((config) => createAuthProvider(config));
}
